import React from "react";
import { Check, Caravan, Sparkles, Gem, Star, ArrowRight } from "lucide-react";

const caravans = [
  {
    id: "caravan-basic",
    name: "Caravan Basic",
    tagline: "Regular maintenance & pre/post-trip cleaning.",
    priceFrom: "$350",
    Icon: Caravan,
    image: "https://images.unsplash.com/photo-1744287970928-b12cd6429439",
    items: [
      "Full exterior pressure/foam wash",
      "Roof wash",
      "Windows cleaned",
      "Wheels, tyres & wheel arches washed",
      "Awning cleaned",
      "Front, rear & side panels washed",
      "Hand dry",
      "Tyre dressing",
      "Exterior plastics dressed",
    ],
    sizes: [
      { label: "Up to 18 ft", price: "$350" },
      { label: "19 – 22 ft", price: "$400" },
      { label: "23 – 26 ft", price: "$450" },
      { label: "27 ft+", price: "Quote" },
    ],
  },
  {
    id: "caravan-polish",
    name: "Caravan Wash & Polish",
    tagline: "Our best-value package for restoring gloss.",
    priceFrom: "$750",
    badge: "Most Popular",
    Icon: Sparkles,
    image: "https://images.unsplash.com/photo-1626680114529-3f6ffa002b80",
    items: [
      "Everything in Caravan Basic",
      "Full exterior decontamination",
      "Iron / contaminant removal",
      "Machine polish",
      "Paint & gelcoat gloss enhancement",
      "Light oxidation removal",
      "Removal of light swirls & surface marks",
      "Exterior windows polished",
      "Protective sealant applied",
    ],
    sizes: [
      { label: "Up to 18 ft", price: "$750" },
      { label: "19 – 22 ft", price: "$850" },
      { label: "23 – 26 ft", price: "$950" },
      { label: "27 ft+", price: "from $1,100" },
    ],
  },
  {
    id: "caravan-ultimate",
    name: "Caravan Ultimate + Ceramic",
    tagline: "Complete rejuvenation + ceramic protection.",
    priceFrom: "$1,500",
    Icon: Gem,
    image: "https://images.unsplash.com/photo-1513311068348-19c8fbdc0bb6",
    items: [
      "Everything in Wash & Polish",
      "Full paint / gelcoat correction",
      "Oxidation reduction (where safely possible)",
      "Deep surface decontamination",
      "Machine polish & refinement",
      "Ceramic coating applied to exterior",
      "Hydrophobic & UV protection",
      "Enhanced gloss & easier maintenance",
      "Final inspection",
    ],
    sizes: [
      { label: "Up to 18 ft", price: "$1,500" },
      { label: "19 – 22 ft", price: "$1,750" },
      { label: "23 – 26 ft", price: "$2,000" },
      { label: "27 ft+", price: "Quote" },
    ],
  },
];

export default function CaravanServices() {
  return (
    <section
      id="caravans"
      data-testid="caravan-services-section"
      className="section bg-[color:var(--bg)] relative"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col gap-6 lg:grid lg:grid-cols-12 lg:items-end mb-12">
          <div className="col-span-12 lg:col-span-7">
            <div className="flex items-center gap-3 mb-6 reveal">
              <span className="diamond-divider" />
              <span className="overline">Caravan Detailing</span>
            </div>
            <h2 className="reveal font-display tracking-tight text-neutral-900 text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
              Caravans, cleaned
              <br />
              <span className="text-neutral-500 font-light italic">
                to showroom standard.
              </span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5 reveal">
            <p className="text-neutral-600 leading-relaxed">
              Mobile caravan detailing — pre-trip wash, polish, or full ceramic
              protection. Prices from · final price depends on caravan length,
              condition and oxidation.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-200">
          {caravans.map((c) => (
            <article
              key={c.id}
              data-testid={`caravan-card-${c.id}`}
              className={`relative bg-white p-8 group hover:bg-neutral-50 transition-colors reveal flex flex-col ${
                c.id === "caravan-polish"
                  ? "ring-1 ring-[color:var(--accent)]/60"
                  : ""
              }`}
            >
              <div className="aspect-[16/10] overflow-hidden mb-6 -mx-8 -mt-8">
                <img
                  src={c.image}
                  alt={c.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              </div>

              {c.badge && (
                <div className="inline-flex items-center gap-2 px-3 py-1 border border-[color:var(--accent)]/60 text-[color:var(--accent)] text-[10px] tracking-[0.25em] uppercase mb-3 self-start">
                  <Star size={12} /> {c.badge}
                </div>
              )}

              <div className="flex items-center gap-2 text-[color:var(--accent)]">
                <c.Icon size={18} strokeWidth={1.5} />
                <span className="overline text-[color:var(--accent)]">
                  {c.tagline}
                </span>
              </div>

              <h3 className="font-display text-neutral-900 text-2xl md:text-[26px] font-semibold mt-3 leading-tight">
                {c.name}
              </h3>

              <ul className="mt-5 space-y-2 flex-1">
                {c.items.map((i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2.5 text-sm text-neutral-700"
                  >
                    <Check
                      size={14}
                      className="text-[color:var(--accent)] mt-1 shrink-0"
                    />
                    <span>{i}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 pt-5 border-t border-neutral-200">
                <div className="overline mb-3">Size-based pricing</div>
                <div className="space-y-1.5">
                  {c.sizes.map((s) => (
                    <div
                      key={s.label}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="text-neutral-600">{s.label}</span>
                      <span className="text-neutral-900 font-medium font-mono">
                        {s.price}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-neutral-200">
                <div className="overline">From</div>
                <div className="flex items-end justify-between mt-1">
                  <div className="font-display text-neutral-900 text-4xl font-bold tracking-tight">
                    {c.priceFrom}
                  </div>
                  <a
                    href="#book"
                    data-testid={`caravan-book-${c.id}`}
                    className="inline-flex items-center gap-1 text-[color:var(--accent)] text-sm font-medium hover:text-neutral-900 transition-colors"
                  >
                    Book this <ArrowRight size={14} />
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>

        <p className="mt-6 text-xs text-neutral-400 reveal">
          Heavy oxidation restoration is quoted separately based on inspection.
        </p>
      </div>
    </section>
  );
}
